import { OverlayContainer } from "components";
import React, { FC, memo, useCallback, useEffect, useState } from "react";
import { MatchStatus } from "service";
import styled from "styled-components";
import { SIDE } from "_constants";
import { getTeamColors } from "components/colors";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { GoalIndicator } from "./GoalIndicator";
import { Text } from "components/common/Text";

const ANIMATION_DURATION = 500;
const GOAL_INDICATOR_DURATION = 5000;

export const ScoreIndicator: FC<{ status: MatchStatus }> = memo(
  ({ status }) => {
    const { c, nsc, side, T1, T2, t1, t2, T1n, T2n } = status as any;

    const _GOAL = c?.GOAL as [number, number] | undefined;
    const [_homeScore, _awayScore] = Array.isArray(_GOAL) ? _GOAL : [0, 0];

    const [
      {
        score: [homeScore, awayScore],
        toggle,
        isActive,
      },
      setState,
    ] = useState({
      score: [_homeScore, _awayScore] as [number, number],
      toggle: false,
      isActive: false,
    });

    const [showGoalIndicator, setShowGoalIndicator] = useState(false);

    const activate = useCallback(() => {
      setState((s) => ({ ...s, isActive: true, toggle: false }));
      setTimeout(() => {
        setState((s) => ({ ...s, toggle: true }));
      }, ANIMATION_DURATION);
    }, []);

    useEffect(() => {
      if (homeScore === _homeScore && awayScore === _awayScore) return;
      if (nsc !== "1003" || isActive || toggle) return;

      setShowGoalIndicator(true);
      const timer = setTimeout(() => {
        setShowGoalIndicator(false);
        activate();
        setTimeout(() => {
          setState((s) => ({
            ...s,
            score: [_homeScore, _awayScore] as [number, number],
          }));
        }, 2 * ANIMATION_DURATION);
      }, GOAL_INDICATOR_DURATION);

      return () => clearTimeout(timer);
    }, [
      _homeScore,
      _awayScore,
      homeScore,
      awayScore,
      nsc,
      isActive,
      toggle,
      activate,
    ]);

    useEffect(() => {
      if (nsc !== "1003") {
        setState((s) => ({
          ...s,
          isActive: false,
          toggle: false,
          score: [_homeScore, awayScore] as [number, number],
        }));
      }
    }, [nsc, _homeScore, awayScore]);

    const homeTeamName = T1?.n ?? t1?.n ?? T1n ?? "";
    const awayTeamName = T2?.n ?? t2?.n ?? T2n ?? "";

    const { home: homeColor, away: awayColor } = getTeamColors(status as any);

    if (showGoalIndicator) {
      return (
        <GoalIndicator
          side={side as SIDE}
          teamName={side === SIDE.HOME ? homeTeamName : awayTeamName}
          onEnd={() => setShowGoalIndicator(false)}
          status={status as any}
        />
      );
    }

    if (!isActive) return null;

    return (
      <OverlayContainer>
        <InnerContainer>
          <div style={{ display: "flex", margin: "auto", gap: "0.5vw" }}>
            <ScoreWrapper
              side={SIDE.HOME}
              className={toggle ? "enter" : ""}
              color={homeColor}
            >
              <Score side={SIDE.HOME} score={homeScore} color={homeColor} />
            </ScoreWrapper>
            <ScoreWrapper
              side={SIDE.AWAY}
              className={toggle ? "enter" : ""}
              color={awayColor}
            >
              <Score side={SIDE.AWAY} score={awayScore} color={awayColor} />
            </ScoreWrapper>
          </div>
        </InnerContainer>
      </OverlayContainer>
    );
  }
);

const ScoreWrapper = styled.div<{ side: SIDE; color: string }>`
  transform: translateX(${(p) => (p.side === SIDE.HOME ? -1 : 1) * 6}vw);
  border-top: 1vw solid ${(p) => p.color};

  &.enter {
    transform: translateX(0);
    transition: transform ${ANIMATION_DURATION}ms ease;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  margin: auto;
  width: 40vw;
  height: 10vw;
  overflow: hidden;
`;

const Score: FC<{
  score?: number;
  side: SIDE;
  className?: string;
  color: string;
}> = memo(({ score: _score = 0, side, className, color }) => {
  const [score, setScore] = useState<number>(_score);
  useEffect(() => setScore(_score), [_score]);

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        key={score}
        addEndListener={(node, done) =>
          node.addEventListener("transitionend", done, false)
        }
        classNames="score"
      >
        <ScoreContainer side={side} className={className} color={color}>
          <Text variant="soccerScoreEffectText">{score}</Text>
        </ScoreContainer>
      </CSSTransition>
    </SwitchTransition>
  );
});

const ScoreContainer = styled.div<{ side: SIDE; color: string }>`
  background: rgba(0, 0, 0, 0.75);
  box-shadow: 0 0 1vw rgba(0, 0, 0, 0.5);
  text-align: center;
  padding: 2vw;
  width: 10vw;
  line-height: 0.95;
  color: #ffffff;

  &.score-enter {
    transform: translateX(0) translateY(11vw);
  }
  &.score-enter-active {
    transform: translateX(0) translateY(0);
  }
  &.score-exit {
    transform: translateX(0) translateY(0);
  }
  &.score-exit-active {
    transform: translateX(0) translateY(-11vw);
  }
  transition: transform ${ANIMATION_DURATION}ms ease;
`;
